import { INPUT_SAMPLE_RATE } from '../../shared/config.ts';
import { every, sleep } from './clock.ts';
import { floatToInt16, int16ToBase64 } from './pcm.ts';

/** Where user audio comes from. Mic in real use; PCM fixtures in tests (deterministic, multi-turn). */
export interface AudioSource {
  readonly kind: 'mic' | 'fixture';
  start(onChunk: (b64: string) => void, onUtteranceEnd?: () => void): Promise<void>;
  /** Called on every model turnComplete. Fixture sources play their next utterance here. */
  onModelTurnComplete?(): void;
  /** Called once a speaking task is open and the model has gone quiet. Fixture sources play their task answer here. */
  onTaskStarted?(): void;
  /** Called when the open speaking task closes (done, skipped or cancelled). Fixture sources stop its answer here. */
  onTaskClosed?(): void;
  stop(): void;
}

// Runs on the audio thread: forwards 40 ms Float32 frames to the main thread.
const WORKLET = `
class Capture extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(${INPUT_SAMPLE_RATE / 25}); this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    for (let i = 0; i < ch.length; i++) {
      this.buf[this.n++] = ch[i];
      if (this.n === this.buf.length) { this.port.postMessage(this.buf.slice(0)); this.n = 0; }
    }
    return true;
  }
}
registerProcessor('fuse-capture', Capture);
`;

export class MicAudioSource implements AudioSource {
  readonly kind = 'mic' as const;
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;

  async start(onChunk: (b64: string) => void) {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    });
    // The context resamples the mic to 16 kHz for us.
    this.ctx = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE });
    await this.ctx.audioWorklet.addModule(URL.createObjectURL(new Blob([WORKLET], { type: 'text/javascript' })));
    const node = new AudioWorkletNode(this.ctx, 'fuse-capture');
    node.port.onmessage = (e: MessageEvent<Float32Array>) => onChunk(int16ToBase64(floatToInt16(e.data)));
    this.ctx.createMediaStreamSource(this.stream).connect(node);
  }

  stop() {
    for (const t of this.stream?.getTracks() ?? []) t.stop();
    void this.ctx?.close();
    this.ctx = null;
    this.stream = null;
  }
}

/**
 * Streams raw 16 kHz PCM fixtures (e2e/fixtures/audio/*.pcm) in real time, followed by silence so server-side VAD
 * detects end of speech: one utterance per model turn, and a task clip (`task:<name>`) for each speaking task. Enabled
 * with ?audio=fixture:<a>,<b> (e2e), or an admin's Answer audio mock, whose one clip answers every task (`everyTask`).
 */
export class PcmFixtureAudioSource implements AudioSource {
  readonly kind = 'fixture' as const;
  // "name" plays after a model turn; "task:name" plays when a speaking task opens and the model has gone quiet.
  private turnQueue: string[];
  private taskQueue: string[];
  private stopTimer: (() => void) | null = null;
  private onChunk: ((b64: string) => void) | null = null;
  private onUtteranceEnd: (() => void) | undefined;
  private playing = false;
  private stopped = false;
  // Bumped when a task opens or closes: a task clip still loading or streaming for an earlier one stops, so the end of
  // one answer never leaks into the next task's recording, or into the call as user speech.
  private taskCue = 0;
  private readonly baseUrl: string;
  // An admin's mock picks one clip for the call: it answers every task, not just the first (e2e queues one per task).
  private readonly everyTask: boolean;

  constructor(names: string[], baseUrl = '/__fixtures/audio', opts: { everyTask?: boolean } = {}) {
    this.turnQueue = names.filter((n) => !n.startsWith('task:'));
    this.taskQueue = names.filter((n) => n.startsWith('task:')).map((n) => n.slice(5));
    this.baseUrl = baseUrl;
    this.everyTask = opts.everyTask ?? false;
  }

  async start(onChunk: (b64: string) => void, onUtteranceEnd?: () => void) {
    this.onChunk = onChunk;
    this.onUtteranceEnd = onUtteranceEnd;
  }

  onModelTurnComplete() {
    if (this.playing) return;
    const next = this.turnQueue.shift();
    if (next) void this.play(next);
  }

  onTaskStarted() {
    const cue = ++this.taskCue;
    const next = this.everyTask ? this.taskQueue[0] : this.taskQueue.shift();
    if (!next) return;
    // Never interleave two clips: wait for a turn utterance that is still playing.
    const start = async () => {
      while (this.playing) await sleep(100);
      if (!this.stopped && cue === this.taskCue) await this.play(next, cue);
    };
    void start();
  }

  onTaskClosed() {
    this.taskCue++;
  }

  /** `cue`: a task clip, which stops once its task is no longer the open one. */
  private async play(name: string, cue?: number) {
    const current = () => cue === undefined || cue === this.taskCue;
    this.playing = true;
    const res = await fetch(`${this.baseUrl}/${name}.pcm`).catch(() => null);
    if (!res?.ok) {
      // Say so, instead of playing an error page as noise and leaving the task to fail as "too short".
      console.error(`audio clip ${name} failed to load (${res?.status ?? 'network'})`);
      this.playing = false;
      return;
    }
    const speech = new Int16Array(await res.arrayBuffer());
    // Stopped while the clip loaded (the call ended or restarted, or its task closed): don't stream it into what's next.
    if (this.stopped || !current()) {
      this.playing = false;
      return;
    }
    const silence = new Int16Array(INPUT_SAMPLE_RATE * 2);
    const all = new Int16Array(speech.length + silence.length);
    all.set(speech);
    const frame = INPUT_SAMPLE_RATE / 25; // 40 ms
    let offset = 0;
    // Paced on the worker clock: the page's own timers crawl while the tab is hidden (see clock.ts).
    await new Promise<void>((resolve) => {
      this.stopTimer = every(40, () => {
        if (offset >= all.length || !current()) {
          this.stopTimer?.();
          resolve();
          return;
        }
        this.onChunk?.(int16ToBase64(all.subarray(offset, offset + frame)));
        offset += frame;
      });
    });
    this.playing = false;
    this.onUtteranceEnd?.();
  }

  stop() {
    this.stopTimer?.();
    this.stopped = true;
    this.playing = false;
    this.turnQueue = [];
    this.taskQueue = [];
  }
}
